import React, { useState, useEffect, useMemo } from "react";
import { useTheme } from "../components/ThemeContext";
import { Table, Button, Spinner } from "react-bootstrap";
import NavMenu from "../components/NavMenu";
import { FaEdit } from "react-icons/fa";
import { GiPadlock } from "react-icons/gi";
import axios from "axios";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { useNavigate } from "react-router-dom";
import photo from "../img/no-data.svg";
import FloatingActionMenu from "../components/FloatingActionButton";

function SupDashboard() {
  const [staff, setStaff] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [id, setID] = useState("");
  const [department, setDepartment] = useState("");
  const [allowedIds, setAllowedIds] = useState([]);

  const navigate = useNavigate();
  const { isDark } = useTheme();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?.id) {
      setID(user.id);
      setDepartment(user.description);
    }
  }, []);

  const formatRating = (v) => {
    const n = Number(v);
    if (Number.isNaN(n)) return "0.00";
    return n.toFixed(2);
  };

  useEffect(() => {
    if (!id) return;

    const user = JSON.parse(localStorage.getItem("user"));

    const fetchRatings = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found in localStorage");
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}/getAllAppraisal`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.success) {
          const filtered = response.data.data.filter(
            (item) => item.supervisor_name === user.user_name
          );
          setRatings(filtered);
        } else {
          console.error(response.data.message);
        }
      } catch (error) {
        console.error("Error fetching ratings:", error);
      }
    };

    fetchRatings();
  }, [id]);

  useEffect(() => {
    if (!id) return;

    const fetchStaff = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found in localStorage");
      }

      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API}/getSendAppraisal?supervisor_id=${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.success) {
          setStaff(response.data.data);
          const allowed = response.data.data
            .filter((emp) => emp.allow === 1)
            .map((emp) => emp.send_id);
          setAllowedIds(allowed);
        } else {
          console.error(response.data.message);
        }
      } catch (error) {
        console.error("Error fetching staff data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStaff();
  }, [id]);

  const handleAllowToggle = async (emp, checked) => {
    const confirm = window.confirm(
      `Are you sure you want to ${checked ? "allow" : "disallow"} this appraisal form?`
    );
    if (!confirm) return;

    const token = localStorage.getItem("authToken");
    if (!token) {
      console.error("No auth token found in localStorage");
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}/updateAllow`,
        {
          send_id: emp.send_id,
          allow: checked ? 1 : 0,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        if (checked) {
          setAllowedIds((prev) => [...prev, emp.send_id]);
        } else {
          setAllowedIds((prev) => prev.filter((x) => x !== emp.send_id));
        }
      } else {
        console.error(response.data.message);
      }
    } catch (error) {
      console.error("Error updating allow:", error);
    }
  };

  const getStatusStyle = (status) => {
    switch ((status || "").toLowerCase()) {
      case "submitted":
        return { bg: "#e6ffe6", text: "#2e7d32", dot: "#4caf50" };
      case "employee signed":
        return { bg: "#d0f0fd", text: "#0077b6", dot: "#0096c7" };
      case "both signed":
        return { bg: "#f3e5f5", text: "#6a1b9a", dot: "#9c27b0" };
      case "reviewing":
        return { bg: "#fff3e0", text: "#ef6c00", dot: "#ff9800" };
      case "pending":
        return { bg: "#ffebee", text: "#c62828", dot: "#f44336" };
      default:
        return { bg: "#eceff1", text: "#546e7a", dot: "#90a4ae" };
    }
  };

  const handleView = (item) => {
    navigate(`/edit-assessment`, {
      state: {
        staff_id: item.staff_id,
        send_id: item.send_id,
        unique_doc_id: item.unique_doc_id,
      },
    });
  };

  const ratingByStaffId = useMemo(() => {
    const map = new Map();
    (ratings || []).forEach((r) => {
      const key = r.staff_id ?? r.emp_id ?? r.employee_id;
      if (key == null) return;
      map.set(String(key), {
        appraisal_name:
          r.appraisal_name ||
          r.pa_name ||
          r.template_name ||
          r.form_name ||
          r.appraisal_title ||
          "",
        overall_result: r.overall_result,
      });
    });
    return map;
  }, [ratings]);

  // const getEmpRatingInfo = (emp) => {
  //   const key = emp?.staff_id ?? emp?.emp_id ?? emp?.employee_id;
  //   const found = key != null ? ratingByStaffId.get(String(key)) : null;
  //   return {
  //     appraisalName: found?.appraisal_name || emp?.appraisal_name || emp?.pa_name || "-",
  //     ratingText: formatRating(found?.overall_result ?? emp?.overall_result ?? 0),
  //     ratingValue: Number(found?.overall_result ?? emp?.overall_result ?? 0) || 0,
  //   };
  // };

  return (
    <>
      <NavMenu />
      <div className="container-fluid p-4">
        <h5 className="mb-3 header-title fw-bold">Supervisor Dashboard</h5>

        <div className="d-flex gap-3">
          <div
            className={`p-3 rounded shadow-sm ${
              isDark ? "bg-dark text-light border border-secondary" : "bg-light text-dark"
            }`}
            style={{ flex: 1, maxWidth: "25%" }}
          >
            <h6 className="fw-bold mb-3">{department} Department</h6>
            <p className={`mb-3 ${isDark ? "text-secondary" : "text-muted"}`}>
              Rating for Each Employee
            </p>

            {ratings.map((r, idx) => {
              const appraisalName =
                r.appraisal_name ||
                r.pa_name ||
                r.template_name ||
                r.form_name ||
                r.appraisal_title ||
                "-";

              const ratingText = formatRating(r.overall_result ?? 0);
              const ratingValue = Number(r.overall_result ?? 0) || 0;

              return (
                <div
                  key={idx}
                  className={`border-bottom py-2 ${isDark ? "border-secondary" : ""}`}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div style={{ minWidth: 0 }}>
                      <div className="fw-semibold text-truncate">{r.staff_name}</div>
                      <div
                        className={`small text-truncate ${
                          isDark ? "text-secondary" : "text-muted"
                        }`}
                        title={appraisalName}
                      >
                        {appraisalName}
                      </div>
                    </div>

                    <div style={{ width: 52, height: 52 }}>
                      <CircularProgressbar
                        value={ratingValue * 20}
                        text={ratingText}
                        styles={buildStyles({
                          textSize: "24px",
                          pathColor: "#B31E8D",
                          textColor: isDark ? "#fff" : "#000",
                          trailColor: isDark ? "#555" : "#eee",
                        })}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div
            className={`p-3 rounded shadow-sm ${
              isDark ? "bg-dark text-light border border-secondary" : "bg-light text-dark"
            }`}
            style={{ flex: 3 }}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold">Amend Performance Appraisal</h6>
            </div>

            {loading ? (
              <div className="text-center">
                <Spinner animation="border" variant={isDark ? "light" : "primary"} />
              </div>
            ) : (
              <Table hover responsive className={`align-middle ${isDark ? "table-dark" : ""}`}>
                <thead>
                  <tr className="text-center">
                    <th>No</th>
                    <th>Name</th>
                    <th>Department</th>
                    <th>Supervisor</th>
                    <th>Status</th>
                    <th>Allow</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {staff.length > 0 ? (
                    staff.map((emp, index) => {

                      return (
                        <tr key={index} className="align-middle text-center">
                          <td>{index + 1}</td>

                          <td className="text-start">
                            <div className="fw-semibold">{emp.staff_name || "-"}</div>
                            <div
                              className={`small ${isDark ? "text-secondary" : "text-muted"}`}
                            >
                              {emp.appraisal_name}
                            </div>
                          </td>

                          <td>{emp.department || "-"}</td>
                          <td>{emp.supervisor_name || "-"}</td>

                          <td>
                            {(() => {
                              const { bg, text, dot } = getStatusStyle(emp.status);
                              return (
                                <div
                                  className="d-inline-flex align-items-center px-2 py-1 rounded-pill"
                                  style={{
                                    backgroundColor: bg,
                                    color: text,
                                    fontWeight: "500",
                                    textTransform: "capitalize",
                                    fontSize: "12px",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "10px",
                                      height: "10px",
                                      backgroundColor: dot,
                                      borderRadius: "50%",
                                      marginRight: "8px",
                                    }}
                                  />
                                  {emp.status || "-"}
                                </div>
                              );
                            })()}
                          </td>

                          <td>
                            <input
                              type="checkbox"
                              checked={allowedIds.includes(emp.send_id)}
                              onChange={(e) => handleAllowToggle(emp, e.target.checked)}
                            />
                          </td>

                          <td>
                            <div className="d-flex justify-content-center gap-2">
                              <Button
                                variant="outline-light"
                                size="sm"
                                style={{
                                  color: "#B31E8D",
                                  borderColor: "#B31E8D",
                                  backgroundColor: isDark ? "#2c2c2c" : "transparent",
                                  opacity: ["submitted", "employee signed", "both signed"].includes(
                                    emp.status?.toLowerCase()
                                  )
                                    ? 0.5
                                    : 1,
                                }}
                                disabled={["submitted", "employee signed", "both signed"].includes(
                                  emp.status?.toLowerCase()
                                )}
                                onClick={() => handleView(emp)}
                              >
                                {["submitted", "employee signed", "both signed"].includes(
                                  emp.status?.toLowerCase()
                                ) ? (
                                  <>
                                    <GiPadlock size={20} />
                                    &nbsp;Locked
                                  </>
                                ) : (
                                  <>
                                    <FaEdit size={20} />
                                    &nbsp;Amend PA Assessment
                                  </>
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <div className="d-flex flex-column align-items-center">
                          <img
                            src={photo}
                            alt="Illustration"
                            className="img-fluid mb-3"
                            width="100px"
                          />
                          <div className={isDark ? "text-secondary" : "text-muted"}>
                            No PA Form found
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </div>
        </div>
      </div>

      <FloatingActionMenu />
    </>
  );
}

export default SupDashboard;
